/**
 * The chunk-recovery mark: whether a loader recently owned a chunk failure
 * (2026-09-26, split out of lazyWithRetry.ts).
 *
 * App.tsx's global unhandledrejection handler suppresses a chunk rejection only
 * while this reads active (lazyWithRetry.ts `handleChunkRejectionEvent`). A
 * loader that recovers by itself marks it with `noteChunkRecoveryActivity`
 * (the chat skin's door, the thread namer's door); `retryImport` marks it on
 * every chunk-class attempt.
 *
 * WHY A FILE OF ITS OWN, WITH NO IMPORTS. lazyWithRetry.ts reaches the stale-tab
 * self-heal, which reaches the update banner, a React component. A loader in
 * the chat's thread store (the namer's door) importing the mark from there put
 * that component on the store's import graph, and eight thread-store suites
 * that stub React died loading it before a single test ran. The mark's state
 * lives here, once; lazyWithRetry.ts re-exports what it always exported, so
 * every existing caller is unchanged and both share the one timestamp.
 */

/** When a loader last handled a chunk-classified failure. A timestamp window
 * rather than a boolean because the global unhandledrejection event can
 * arrive AFTER a loader's final throw. */
let lastRecoveryActivityAt = 0;

/** Mark recovery active now. `retryImport` calls it on every chunk-class attempt. */
export function markChunkRecoveryActivity(): void {
  lastRecoveryActivityAt = Date.now();
}

/**
 * Whether a loader recently owned a chunk failure. App.tsx's global
 * unhandledrejection handler suppresses chunk errors only while this reads
 * true — a chunk rejection with no recovery in flight (a bare import()?) is
 * a real failure that must stay loud.
 */
export function chunkRecoveryRecentlyActive(windowMs = 30_000): boolean {
  return Date.now() - lastRecoveryActivityAt < windowMs;
}

// Chunk-class = worth retrying AND worth a recovery reload. Broad message
// matching is safe HERE because it is scoped to an actual import attempt —
// the same strings are far too broad for a global rejection filter.
export function isChunkClassError(error: any): boolean {
  return (
    error?.name === 'ChunkLoadError' ||
    error?.message?.includes('Loading chunk') ||
    error?.message?.includes('Failed to fetch') ||
    error?.message?.includes('network') ||
    error?.message?.includes('Unexpected token') || // HTML served where JS was expected
    error?.message?.includes('HTML')
  );
}

/**
 * Mark a chunk failure that a loader recovers from ITSELF, without retryImport's
 * reload (2026-09-25). The new design's chat skin (src/design/pavilionChatDoor.ts)
 * tries its chunk twice and then keeps today's look: a skin must never reload the
 * page. Its own promise chain handles the failure, but webpack's prefetch runtime
 * leaves one more promise per chunk load with no rejection handler, so the same
 * ChunkLoadError also reaches App.tsx's global unhandledrejection handler — which
 * suppresses it only while recovery reads active here. Measured on prest0.ai with
 * the skin's chunk served as html: an uncaught page error and "no active
 * lazyWithRetry recovery", for a failure the chat had already absorbed.
 *
 * Only a chunk-class failure is marked. The window is global, and a module's own
 * bug — or a genuinely unowned import() failing in the same half-minute — must
 * stay loud. Returns whether it marked.
 */
export function noteChunkRecoveryActivity(error: unknown): boolean {
  if (!isChunkClassError(error)) return false;
  markChunkRecoveryActivity();
  return true;
}
